// @src/pre-deployment/src/components/upload/UploadChapterLocations.js v1.2
// - Complete CRUD operations for chapter locations management
// - Added Firebase Storage image upload with progress tracking
// - Added dialogue type selection when assigning dialogue modules
// - Added module assignment UI with drag-drop ordering
// - Enhanced admin interface for location configuration

import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import React, { useState, useEffect, useCallback } from 'react';

import {
  ChapterImageStorageService,
  DIALOGUE_TYPES,
  DIALOGUE_TYPE_MAP,
  MODULE_KEY_TO_DIALOGUE_TYPE
} from '../../services/ChapterImageStorageService';
import { ChapterLocationService } from '../../services/ChapterLocationService';
import { db } from '../../services/firebase';
import { uploadAndVerifyChapterLocations } from '../../utils/uploadChapterLocations';

const UploadChapterLocations = () => {
  // State management
  const [selectedLevel, setSelectedLevel] = useState(2);
  const [selectedChapter, setSelectedChapter] = useState(1);
  const [chapterName, setChapterName] = useState('');
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState('');
  const [showPreview, setShowPreview] = useState(false);
  const [editingLocationIndex, setEditingLocationIndex] = useState(null);

  // New location form state
  const [newLocation, setNewLocation] = useState({
    locationId: '',
    nameKo: '',
    nameEn: '',
    backgroundImage: '',
    thumbnailImage: '',
    order: 1,
    modules: []
  });

  // Image upload state
  const [uploadingBackground, setUploadingBackground] = useState(false);
  const [uploadingThumbnail, setUploadingThumbnail] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({ background: 0, thumbnail: 0 });
  const [previewImages, setPreviewImages] = useState({ background: null, thumbnail: null });

  // Available modules from LearningModulesOrder (for module selection)
  const [availableModules, setAvailableModules] = useState([]);

  // Dialogue type map for each location - tracks which dialogue type is selected
  // Format: { locationIndex: dialogueType } or { 'new': dialogueType } for new location form
  const [dialogueTypeSelection, setDialogueTypeSelection] = useState({});

  const LOAD_TIMEOUT_MS = 10000;

  // Load chapter locations from Firebase
  const loadChapterLocations = useCallback(async () => {
    console.group('📍 UploadChapterLocations: Loading chapter locations');
    console.log('Level:', selectedLevel, 'Chapter:', selectedChapter);
    try {
      setLoading(true);
      setError(null);

      const docId = `level_${selectedLevel}_chapter_${selectedChapter}`;
      const docRef = doc(db, 'feature_testing/Korea_University/ChapterLocations', docId);
      console.log('Firestore path:', `feature_testing/Korea_University/ChapterLocations/${docId}`);

      const timeoutPromise = new Promise((_, reject) =>
        setTimeout(() => reject(new Error('Load chapter locations timeout')), LOAD_TIMEOUT_MS)
      );
      const docSnap = await Promise.race([getDoc(docRef), timeoutPromise]);

      if (docSnap.exists()) {
        const data = docSnap.data();
        console.log('✅ Document found:', {
          chapterName: data.chapterName,
          locationsCount: data.locations?.length || 0
        });
        setChapterName(data.chapterName || '');

        if (data.locations && Array.isArray(data.locations)) {
          // Sort by order
          const sortedLocations = [...data.locations].sort((a, b) => (a.order || 0) - (b.order || 0));
          setLocations(sortedLocations);

          // Initialize dialogue type selections from existing modules
          const dialogueSelections = {};
          sortedLocations.forEach((loc, index) => {
            if (loc.modules) {
              const dialogueModule = loc.modules.find(m => ChapterImageStorageService.isDialogueModule(m));
              if (dialogueModule && dialogueModule !== 'dialogue') {
                dialogueSelections[index] = MODULE_KEY_TO_DIALOGUE_TYPE[dialogueModule] || 'vocab';
              }
            }
          });
          setDialogueTypeSelection(dialogueSelections);
        } else {
          setLocations([]);
        }
      } else {
        console.log('ℹ️ No existing document, initializing with empty data');
        setChapterName('');
        setLocations([]);
      }
    } catch (err) {
      console.error('❌ Error loading chapter locations:', err);
      setError('Failed to load chapter locations: ' + err.message);
      setLocations([]);
    } finally {
      setLoading(false);
      console.groupEnd();
    }
  }, [selectedLevel, selectedChapter]);

  // Load available modules from LearningModulesOrder
  const loadAvailableModules = useCallback(async () => {
    console.group('📚 UploadChapterLocations: Loading available modules');
    try {
      const docRef = doc(db, 'feature_testing/Korea_University/LearningModulesOrder', 'order');
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        const data = docSnap.data();
        if (data.moduleMap) {
          const modules = Object.keys(data.moduleMap).map(key => ({
            id: key,
            displayName: data.moduleMap[key].displayName || key,
            isDialogue: ChapterImageStorageService.isDialogueModule(key)
          }));
          console.log('✅ Loaded modules:', modules.length);
          console.log('Module list:', modules.map(m => m.id).join(', '));
          setAvailableModules(modules);
        }
      } else {
        console.warn('⚠️ No modules order document found');
      }
    } catch (err) {
      console.error('❌ Error loading available modules:', err);
    }
    console.groupEnd();
  }, []); // Empty dependencies - only depends on setAvailableModules which is stable

  useEffect(() => {
    loadAvailableModules();
  }, [loadAvailableModules]);

  // Load data when level/chapter changes
  useEffect(() => {
    loadChapterLocations();
  }, [selectedLevel, selectedChapter, loadChapterLocations]);

  // Save chapter locations to Firebase
  const saveChapterLocations = async () => {
    console.group('💾 UploadChapterLocations: Saving chapter locations');
    console.log('Level:', selectedLevel, 'Chapter:', selectedChapter);
    console.log('Locations count:', locations.length);

    try {
      setLoading(true);
      setError(null);
      setSuccess('');

      const docId = `level_${selectedLevel}_chapter_${selectedChapter}`;
      const docRef = doc(db, 'feature_testing/Korea_University/ChapterLocations', docId);

      // Validate locations
      if (!locations || locations.length === 0) {
        setError('Please add at least one location');
        console.error('❌ Validation failed: No locations');
        console.groupEnd();
        return;
      }

      // Ensure all locations have required fields
      const validatedLocations = locations.map((loc, index) => ({
        ...loc,
        order: loc.order || index + 1,
        modules: loc.modules || []
      }));

      const payload = {
        chapterName: chapterName || `Level ${selectedLevel} Chapter ${selectedChapter}`,
        locations: validatedLocations,
        levelId: `level_${selectedLevel}`,
        chapterId: `chapter_${selectedChapter}`,
        lastUpdated: serverTimestamp(),
        last_updated_by: 'admin_interface',
        last_updated_level: selectedLevel,
        last_updated_chapter: selectedChapter
      };

      console.log('Payload:', JSON.stringify(payload, null, 2));
      await setDoc(docRef, payload, { merge: true });

      // Clear the cache so ChapterPage immediately sees the updates
      ChapterLocationService.clearCache();
      console.log('🗑️ Cache cleared');

      console.log('✅ Successfully saved to Firestore');
      setSuccess(`✅ Successfully saved Level ${selectedLevel} Chapter ${selectedChapter}`);
      setTimeout(() => setSuccess(''), 3000);

    } catch (err) {
      console.error('❌ Error saving chapter locations:', err);
      setError('Failed to save chapter locations: ' + err.message);
    } finally {
      setLoading(false);
      console.groupEnd();
    }
  };

  // Handle image file selection and upload
  const handleImageUpload = async (file, type) => {
    console.group(`📤 UploadChapterLocations: Uploading ${type} image`);
    console.log('File:', file.name, 'Size:', file.size, 'Type:', file.type);

    // Validate file
    const validation = ChapterImageStorageService.validateImageFile(file);
    if (!validation.valid) {
      console.error('❌ Validation failed:', validation.error);
      setError(validation.error);
      console.groupEnd();
      return;
    }

    // Validate locationId is set
    if (!newLocation.locationId.trim()) {
      setError('Please enter a Location ID before uploading images');
      console.error('❌ Location ID is required');
      console.groupEnd();
      return;
    }

    const setUploading = type === 'background' ? setUploadingBackground : setUploadingThumbnail;
    setUploading(true);
    setUploadProgress(prev => ({ ...prev, [type]: 0 }));

    try {
      const result = await ChapterImageStorageService.uploadImage(
        file,
        selectedLevel,
        selectedChapter,
        newLocation.locationId,
        type,
        (progress) => {
          setUploadProgress(prev => ({ ...prev, [type]: progress }));
        }
      );

      if (result.success) {
        console.log('✅ Upload successful:', result.downloadURL);

        // Update the form field with the download URL
        const fieldName = type === 'background' ? 'backgroundImage' : 'thumbnailImage';
        setNewLocation(prev => ({ ...prev, [fieldName]: result.downloadURL }));

        // Set preview
        setPreviewImages(prev => ({ ...prev, [type]: result.downloadURL }));

        setSuccess(`✅ ${type} image uploaded successfully`);
        setTimeout(() => setSuccess(''), 3000);
      } else {
        console.error('❌ Upload failed:', result.error);
        setError(`Failed to upload ${type} image: ${result.error}`);
      }
    } catch (err) {
      console.error('❌ Upload exception:', err);
      setError(`Upload error: ${err.message}`);
    } finally {
      setUploading(false);
      console.groupEnd();
    }
  };

  // Handle file input change
  const handleFileSelect = (e, type) => {
    const file = e.target.files?.[0];
    if (file) {
      handleImageUpload(file, type);
    }
  };

  // Add new location
  const handleAddLocation = () => {
    console.group('➕ UploadChapterLocations: Adding new location');

    if (!newLocation.locationId.trim() || !newLocation.nameKo.trim() || !newLocation.nameEn.trim()) {
      setError('Please fill in locationId, nameKo, and nameEn');
      console.error('❌ Validation failed: Missing required fields');
      console.groupEnd();
      return;
    }

    const location = {
      ...newLocation,
      order: locations.length + 1,
      modules: newLocation.modules || []
    };

    console.log('New location:', location);
    setLocations([...locations, location]);

    // Reset form
    setNewLocation({
      locationId: '',
      nameKo: '',
      nameEn: '',
      backgroundImage: '',
      thumbnailImage: '',
      order: locations.length + 2,
      modules: []
    });
    setPreviewImages({ background: null, thumbnail: null });
    setDialogueTypeSelection(prev => {
      const updated = { ...prev };
      delete updated['new'];
      return updated;
    });
    setError(null);

    console.log('✅ Location added successfully');
    console.groupEnd();
  };

  // Update location
  const handleUpdateLocation = (index) => {
    console.group('✏️ UploadChapterLocations: Updating location');
    console.log('Index:', index, 'Location:', newLocation);

    const updated = [...locations];
    updated[index] = { ...newLocation };
    setLocations(updated);
    setEditingLocationIndex(null);

    // Reset form
    setNewLocation({
      locationId: '',
      nameKo: '',
      nameEn: '',
      backgroundImage: '',
      thumbnailImage: '',
      order: 1,
      modules: []
    });
    setPreviewImages({ background: null, thumbnail: null });

    console.log('✅ Location updated successfully');
    console.groupEnd();
  };

  // Delete location
  const handleDeleteLocation = (index) => {
    if (window.confirm('Delete this location?')) {
      console.log('🗑️ Deleting location at index:', index);
      const updated = locations.filter((_, i) => i !== index);
      // Reorder
      updated.forEach((loc, i) => {
        loc.order = i + 1;
      });
      setLocations(updated);
    }
  };

  // Edit location
  const handleEditLocation = (index) => {
    console.log('✏️ Editing location at index:', index);
    const loc = locations[index];
    setNewLocation({ ...loc });
    setEditingLocationIndex(index);
    setPreviewImages({
      background: loc.backgroundImage || null,
      thumbnail: loc.thumbnailImage || null
    });

    // Set dialogue type if exists
    const dialogueModule = loc.modules?.find(m => ChapterImageStorageService.isDialogueModule(m));
    if (dialogueModule && dialogueModule !== 'dialogue') {
      setDialogueTypeSelection(prev => ({
        ...prev,
        'new': MODULE_KEY_TO_DIALOGUE_TYPE[dialogueModule] || 'vocab'
      }));
    }
  };

  // Move location up/down
  const moveLocation = (index, direction) => {
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= locations.length) return;

    const updated = [...locations];
    [updated[index], updated[newIndex]] = [updated[newIndex], updated[index]];

    // Update order
    updated.forEach((loc, i) => {
      loc.order = i + 1;
    });

    setLocations(updated);
  };

  // Toggle module in location (for new location form)
  const toggleModuleInNewLocation = (moduleId) => {
    const currentModules = newLocation.modules || [];
    const isDialogue = ChapterImageStorageService.isDialogueModule(moduleId);

    // If it's a dialogue module being added
    if (isDialogue && moduleId === 'dialogue') {
      // Check if any dialogue variant already exists
      const hasDialogue = currentModules.some(m => ChapterImageStorageService.isDialogueModule(m));

      if (!hasDialogue) {
        // Add dialogue with current selected type
        const dialogueType = dialogueTypeSelection['new'] || 'vocab';
        const moduleKey = DIALOGUE_TYPE_MAP[dialogueType];
        setNewLocation({ ...newLocation, modules: [...currentModules, moduleKey] });
      } else {
        // Remove all dialogue variants
        const filteredModules = currentModules.filter(m => !ChapterImageStorageService.isDialogueModule(m));
        setNewLocation({ ...newLocation, modules: filteredModules });
      }
    } else {
      // Non-dialogue module toggle
      const index = currentModules.indexOf(moduleId);
      if (index > -1) {
        setNewLocation({ ...newLocation, modules: currentModules.filter(m => m !== moduleId) });
      } else {
        setNewLocation({ ...newLocation, modules: [...currentModules, moduleId] });
      }
    }
  };

  // Handle dialogue type change
  const handleDialogueTypeChange = (locationKey, dialogueType) => {
    console.log('🔄 Dialogue type changed:', locationKey, '->', dialogueType);
    setDialogueTypeSelection(prev => ({ ...prev, [locationKey]: dialogueType }));

    // If this is for the new location form, update the modules array
    if (locationKey === 'new') {
      const currentModules = newLocation.modules || [];
      // Remove any existing dialogue modules
      const filteredModules = currentModules.filter(m => !ChapterImageStorageService.isDialogueModule(m));
      // Add the new dialogue type
      const moduleKey = DIALOGUE_TYPE_MAP[dialogueType];
      setNewLocation({ ...newLocation, modules: [...filteredModules, moduleKey] });
    }
  };

  // Check if dialogue is assigned
  const hasDialogueModule = (modules) => {
    return modules?.some(m => ChapterImageStorageService.isDialogueModule(m)) || false;
  };

  // Get current dialogue type for display
  const getCurrentDialogueType = (modules) => {
    const dialogueModule = modules?.find(m => ChapterImageStorageService.isDialogueModule(m));
    if (dialogueModule && dialogueModule !== 'dialogue') {
      return MODULE_KEY_TO_DIALOGUE_TYPE[dialogueModule] || 'vocab';
    }
    return 'vocab';
  };

  // Move module up/down within location
  const moveModuleInNewLocation = (fromIndex, toIndex) => {
    if (toIndex < 0 || toIndex >= (newLocation.modules?.length || 0)) return;

    const updated = [...(newLocation.modules || [])];
    const [moved] = updated.splice(fromIndex, 1);
    updated.splice(toIndex, 0, moved);

    setNewLocation({ ...newLocation, modules: updated });
  };

  // Remove module from new location
  const removeModuleFromNewLocation = (moduleId) => {
    const currentModules = newLocation.modules || [];
    setNewLocation({ ...newLocation, modules: currentModules.filter(m => m !== moduleId) });
  };

  // Upload default data
  const handleUploadDefault = async () => {
    if (!window.confirm('Upload default chapter locations data? This will overwrite existing data.')) return;

    try {
      setLoading(true);
      setError(null);

      const result = await uploadAndVerifyChapterLocations();

      if (result.success) {
        setSuccess('✅ Default data uploaded successfully!');
        await loadChapterLocations();
      } else {
        setError('Failed to upload: ' + (result.error || 'Unknown error'));
      }
    } catch (err) {
      setError('Upload failed: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  // Get module display name
  const getModuleDisplayName = (moduleId) => {
    if (ChapterImageStorageService.isDialogueModule(moduleId)) {
      return ChapterImageStorageService.getDialogueDisplayName(moduleId);
    }
    const module = availableModules.find(m => m.id === moduleId);
    return module?.displayName || moduleId;
  };

  return (
    <div className="upload-chapter-locations" style={{
      maxWidth: '1400px',
      margin: '0 auto',
      padding: '20px',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif'
    }}>
      <h2 style={{ fontSize: '18px', marginBottom: '20px', color: '#2c3e50' }}>
        Chapter Locations Admin
      </h2>

      {loading && <div style={{ padding: '12px', background: '#e3f2fd', borderRadius: '4px', marginBottom: '15px' }}>Loading...</div>}
      {error && <div style={{ padding: '12px', background: '#f8d7da', color: '#721c24', borderRadius: '4px', marginBottom: '15px' }}>{error}</div>}
      {success && <div style={{ padding: '12px', background: '#d4edda', color: '#155724', borderRadius: '4px', marginBottom: '15px' }}>{success}</div>}

      {/* Level/Chapter Selectors */}
      <div style={{ display: 'flex', gap: '20px', marginBottom: '20px' }}>
        <div style={{ flex: 1 }}>
          <label style={{ display: 'block', fontWeight: 500, marginBottom: '5px' }}>Level:</label>
          <select
            value={selectedLevel}
            onChange={(e) => setSelectedLevel(parseInt(e.target.value, 10))}
            style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
          >
            {[1, 2, 3, 4, 5, 6].map(l => <option key={l} value={l}>Level {l}</option>)}
          </select>
        </div>
        <div style={{ flex: 1 }}>
          <label style={{ display: 'block', fontWeight: 500, marginBottom: '5px' }}>Chapter:</label>
          <select
            value={selectedChapter}
            onChange={(e) => setSelectedChapter(parseInt(e.target.value, 10))}
            style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
          >
            {Array.from({ length: 10 }, (_, i) => i + 1).map(c => <option key={c} value={c}>Chapter {c}</option>)}
          </select>
        </div>
      </div>

      {/* Chapter Name */}
      <div style={{ marginBottom: '20px' }}>
        <label style={{ display: 'block', fontWeight: 500, marginBottom: '5px' }}>Chapter Name:</label>
        <input
          type="text"
          value={chapterName}
          onChange={(e) => setChapterName(e.target.value)}
          placeholder="e.g., Seoul Basics"
          style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
        />
      </div>

      {/* Action Buttons */}
      <div style={{ display: 'flex', gap: '10px', marginBottom: '20px', flexWrap: 'wrap' }}>
        <button
          onClick={saveChapterLocations}
          disabled={loading}
          style={{
            padding: '10px 20px',
            background: '#FFA500',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: loading ? 'not-allowed' : 'pointer',
            fontWeight: 500
          }}
        >
          {loading ? 'Saving...' : 'Save Locations'}
        </button>
        <button
          onClick={() => setShowPreview(!showPreview)}
          style={{
            padding: '10px 20px',
            background: '#6c757d',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer'
          }}
        >
          {showPreview ? 'Hide Preview' : 'Preview Data'}
        </button>
        <button
          onClick={handleUploadDefault}
          disabled={loading}
          style={{
            padding: '10px 20px',
            background: '#17a2b8',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: loading ? 'not-allowed' : 'pointer'
          }}
        >
          Upload Default Data
        </button>
      </div>

      {/* Locations List */}
      <div style={{ marginBottom: '30px' }}>
        <h3 style={{ fontSize: '16px', marginBottom: '15px' }}>Locations ({locations.length})</h3>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          {locations.map((location, index) => (
            <div
              key={index}
              style={{
                padding: '15px',
                background: '#f8f9fa',
                border: '1px solid #e9ecef',
                borderRadius: '6px',
                display: 'grid',
                gridTemplateColumns: 'auto 1fr auto',
                gap: '15px',
                alignItems: 'start'
              }}
            >
              <div style={{ fontWeight: 600, color: '#FFA500' }}>#{location.order}</div>

              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 600, marginBottom: '5px' }}>
                  {location.nameKo} ({location.nameEn})
                </div>
                <div style={{ fontSize: '12px', color: '#6c757d', marginBottom: '5px' }}>
                  ID: {location.locationId}
                </div>

                {/* Image Previews */}
                <div style={{ display: 'flex', gap: '10px', marginBottom: '10px' }}>
                  {location.backgroundImage && (
                    <div>
                      <div style={{ fontSize: '10px', color: '#6c757d', marginBottom: '2px' }}>Background:</div>
                      <img
                        src={location.backgroundImage}
                        alt="Background"
                        style={{ width: '60px', height: '40px', objectFit: 'cover', borderRadius: '4px', border: '1px solid #ddd' }}
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                    </div>
                  )}
                  {location.thumbnailImage && (
                    <div>
                      <div style={{ fontSize: '10px', color: '#6c757d', marginBottom: '2px' }}>Thumbnail:</div>
                      <img
                        src={location.thumbnailImage}
                        alt="Thumbnail"
                        style={{ width: '40px', height: '40px', objectFit: 'cover', borderRadius: '4px', border: '1px solid #ddd' }}
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                    </div>
                  )}
                </div>

                <div style={{ fontSize: '12px' }}>
                  <strong>Modules:</strong>{' '}
                  {location.modules?.map(m => getModuleDisplayName(m)).join(', ') || 'None'}
                </div>
              </div>

              <div style={{ display: 'flex', gap: '5px', flexDirection: 'column' }}>
                <button
                  onClick={() => moveLocation(index, 'up')}
                  disabled={index === 0}
                  style={{ padding: '4px 8px', fontSize: '12px', background: '#6c757d', color: 'white', border: 'none', borderRadius: '3px', cursor: 'pointer', opacity: index === 0 ? 0.5 : 1 }}
                >
                  ↑
                </button>
                <button
                  onClick={() => moveLocation(index, 'down')}
                  disabled={index === locations.length - 1}
                  style={{ padding: '4px 8px', fontSize: '12px', background: '#6c757d', color: 'white', border: 'none', borderRadius: '3px', cursor: 'pointer', opacity: index === locations.length - 1 ? 0.5 : 1 }}
                >
                  ↓
                </button>
                <button
                  onClick={() => handleEditLocation(index)}
                  style={{ padding: '4px 8px', fontSize: '12px', background: '#17a2b8', color: 'white', border: 'none', borderRadius: '3px', cursor: 'pointer' }}
                >
                  Edit
                </button>
                <button
                  onClick={() => handleDeleteLocation(index)}
                  style={{ padding: '4px 8px', fontSize: '12px', background: '#dc3545', color: 'white', border: 'none', borderRadius: '3px', cursor: 'pointer' }}
                >
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Add/Edit Location Form */}
      <div style={{
        padding: '20px',
        background: '#f8f9fa',
        border: '1px solid #e9ecef',
        borderRadius: '6px',
        marginBottom: '20px'
      }}>
        <h3 style={{ fontSize: '16px', marginBottom: '15px' }}>
          {editingLocationIndex !== null ? 'Edit Location' : 'Add New Location'}
        </h3>

        {/* Basic Fields */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', marginBottom: '15px' }}>
          <div>
            <label style={{ display: 'block', fontWeight: 500, marginBottom: '5px' }}>Location ID:</label>
            <input
              type="text"
              value={newLocation.locationId}
              onChange={(e) => setNewLocation({ ...newLocation, locationId: e.target.value })}
              placeholder="e.g., university, cafe"
              style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
            />
          </div>
          <div>
            <label style={{ display: 'block', fontWeight: 500, marginBottom: '5px' }}>Order:</label>
            <input
              type="number"
              value={newLocation.order}
              onChange={(e) => setNewLocation({ ...newLocation, order: parseInt(e.target.value, 10) || 1 })}
              style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
            />
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', marginBottom: '15px' }}>
          <div>
            <label style={{ display: 'block', fontWeight: 500, marginBottom: '5px' }}>Name (Korean):</label>
            <input
              type="text"
              value={newLocation.nameKo}
              onChange={(e) => setNewLocation({ ...newLocation, nameKo: e.target.value })}
              placeholder="e.g., 대학교"
              style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
            />
          </div>
          <div>
            <label style={{ display: 'block', fontWeight: 500, marginBottom: '5px' }}>Name (English):</label>
            <input
              type="text"
              value={newLocation.nameEn}
              onChange={(e) => setNewLocation({ ...newLocation, nameEn: e.target.value })}
              placeholder="e.g., University"
              style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
            />
          </div>
        </div>

        {/* Image Upload Section */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', marginBottom: '15px' }}>
          {/* Background Image */}
          <div>
            <label style={{ display: 'block', fontWeight: 500, marginBottom: '5px' }}>Background Image:</label>
            <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-start' }}>
              <div style={{ flex: 1 }}>
                <input
                  type="file"
                  accept="image/webp,image/jpeg,image/jpg,image/png,image/gif"
                  onChange={(e) => handleFileSelect(e, 'background')}
                  disabled={uploadingBackground || !newLocation.locationId}
                  style={{ width: '100%', marginBottom: '8px' }}
                />
                {uploadingBackground && (
                  <div style={{ height: '4px', background: '#e9ecef', borderRadius: '2px', overflow: 'hidden' }}>
                    <div style={{ height: '100%', background: '#28a745', width: `${uploadProgress.background}%`, transition: 'width 0.3s' }} />
                  </div>
                )}
                <input
                  type="text"
                  value={newLocation.backgroundImage}
                  onChange={(e) => setNewLocation({ ...newLocation, backgroundImage: e.target.value })}
                  placeholder="URL (auto-filled after upload)"
                  style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px', fontSize: '12px' }}
                  readOnly={uploadingBackground}
                />
              </div>
              {previewImages.background && (
                <img
                  src={previewImages.background}
                  alt="Background preview"
                  style={{ width: '80px', height: '60px', objectFit: 'cover', borderRadius: '4px', border: '1px solid #ddd' }}
                />
              )}
            </div>
          </div>

          {/* Thumbnail Image */}
          <div>
            <label style={{ display: 'block', fontWeight: 500, marginBottom: '5px' }}>Thumbnail Image:</label>
            <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-start' }}>
              <div style={{ flex: 1 }}>
                <input
                  type="file"
                  accept="image/webp,image/jpeg,image/jpg,image/png,image/gif"
                  onChange={(e) => handleFileSelect(e, 'thumbnail')}
                  disabled={uploadingThumbnail || !newLocation.locationId}
                  style={{ width: '100%', marginBottom: '8px' }}
                />
                {uploadingThumbnail && (
                  <div style={{ height: '4px', background: '#e9ecef', borderRadius: '2px', overflow: 'hidden' }}>
                    <div style={{ height: '100%', background: '#28a745', width: `${uploadProgress.thumbnail}%`, transition: 'width 0.3s' }} />
                  </div>
                )}
                <input
                  type="text"
                  value={newLocation.thumbnailImage}
                  onChange={(e) => setNewLocation({ ...newLocation, thumbnailImage: e.target.value })}
                  placeholder="URL (auto-filled after upload)"
                  style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px', fontSize: '12px' }}
                  readOnly={uploadingThumbnail}
                />
              </div>
              {previewImages.thumbnail && (
                <img
                  src={previewImages.thumbnail}
                  alt="Thumbnail preview"
                  style={{ width: '60px', height: '60px', objectFit: 'cover', borderRadius: '4px', border: '1px solid #ddd' }}
                />
              )}
            </div>
          </div>
        </div>

        {!newLocation.locationId && (
          <div style={{ fontSize: '12px', color: '#dc3545', marginBottom: '15px' }}>
            ⚠️ Enter a Location ID first to enable image upload
          </div>
        )}

        {/* Module Assignment Section */}
        <div style={{ marginBottom: '15px' }}>
          <label style={{ display: 'block', fontWeight: 500, marginBottom: '10px' }}>Assigned Modules:</label>

          {/* Currently Assigned Modules (Ordered List) */}
          {(newLocation.modules?.length > 0) && (
            <div style={{
              padding: '10px',
              background: 'white',
              border: '1px solid #ddd',
              borderRadius: '4px',
              marginBottom: '10px'
            }}>
              <div style={{ fontSize: '12px', color: '#6c757d', marginBottom: '8px' }}>Order (drag or use arrows):</div>
              {newLocation.modules.map((moduleId, idx) => (
                <div
                  key={moduleId}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '6px 10px',
                    marginBottom: '4px',
                    background: ChapterImageStorageService.isDialogueModule(moduleId) ? '#fff3cd' : '#e3f2fd',
                    borderRadius: '4px',
                    fontSize: '13px'
                  }}
                >
                  <span style={{ fontWeight: 500, color: '#6c757d', minWidth: '20px' }}>{idx + 1}.</span>
                  <span style={{ flex: 1 }}>{getModuleDisplayName(moduleId)}</span>
                  <button
                    onClick={() => moveModuleInNewLocation(idx, idx - 1)}
                    disabled={idx === 0}
                    style={{ padding: '2px 6px', fontSize: '10px', background: '#6c757d', color: 'white', border: 'none', borderRadius: '3px', cursor: 'pointer', opacity: idx === 0 ? 0.5 : 1 }}
                  >
                    ↑
                  </button>
                  <button
                    onClick={() => moveModuleInNewLocation(idx, idx + 1)}
                    disabled={idx === newLocation.modules.length - 1}
                    style={{ padding: '2px 6px', fontSize: '10px', background: '#6c757d', color: 'white', border: 'none', borderRadius: '3px', cursor: 'pointer', opacity: idx === newLocation.modules.length - 1 ? 0.5 : 1 }}
                  >
                    ↓
                  </button>
                  <button
                    onClick={() => removeModuleFromNewLocation(moduleId)}
                    style={{ padding: '2px 6px', fontSize: '10px', background: '#dc3545', color: 'white', border: 'none', borderRadius: '3px', cursor: 'pointer' }}
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* Available Modules (Checkboxes) */}
          <div style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '8px',
            padding: '10px',
            background: 'white',
            border: '1px solid #ddd',
            borderRadius: '4px',
            minHeight: '60px'
          }}>
            {availableModules
              .filter(module => !module.isDialogue || module.id === 'dialogue')
              .map(module => {
                const isDialogue = module.id === 'dialogue';
                const hasDialogue = hasDialogueModule(newLocation.modules);
                const isAssigned = isDialogue
                  ? hasDialogue
                  : (newLocation.modules || []).includes(module.id);

                return (
                  <div key={module.id} style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <button
                      onClick={() => toggleModuleInNewLocation(module.id)}
                      style={{
                        padding: '6px 12px',
                        background: isAssigned ? '#28a745' : '#6c757d',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '12px'
                      }}
                    >
                      {module.displayName}
                    </button>

                    {/* Dialogue Type Selector */}
                    {isDialogue && hasDialogue && (
                      <select
                        value={dialogueTypeSelection['new'] || getCurrentDialogueType(newLocation.modules)}
                        onChange={(e) => handleDialogueTypeChange('new', e.target.value)}
                        style={{
                          padding: '6px 8px',
                          border: '1px solid #ffc107',
                          borderRadius: '4px',
                          background: '#fff3cd',
                          fontSize: '11px',
                          cursor: 'pointer'
                        }}
                      >
                        {DIALOGUE_TYPES.map(type => (
                          <option key={type.value} value={type.value}>
                            {type.label}
                          </option>
                        ))}
                      </select>
                    )}
                  </div>
                );
              })}
          </div>
          <div style={{ fontSize: '12px', color: '#6c757d', marginTop: '5px' }}>
            Click to add/remove modules. Use arrows to reorder. For dialogue, select the type from dropdown.
          </div>
        </div>

        {/* Form Actions */}
        <div style={{ display: 'flex', gap: '10px' }}>
          {editingLocationIndex !== null ? (
            <>
              <button
                onClick={() => handleUpdateLocation(editingLocationIndex)}
                style={{
                  padding: '10px 20px',
                  background: '#28a745',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontWeight: 500
                }}
              >
                Update Location
              </button>
              <button
                onClick={() => {
                  setEditingLocationIndex(null);
                  setNewLocation({
                    locationId: '',
                    nameKo: '',
                    nameEn: '',
                    backgroundImage: '',
                    thumbnailImage: '',
                    order: locations.length + 1,
                    modules: []
                  });
                  setPreviewImages({ background: null, thumbnail: null });
                }}
                style={{
                  padding: '10px 20px',
                  background: '#6c757d',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
            </>
          ) : (
            <button
              onClick={handleAddLocation}
              style={{
                padding: '10px 20px',
                background: '#FFA500',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontWeight: 500
              }}
            >
              Add Location
            </button>
          )}
        </div>
      </div>

      {/* Preview */}
      {showPreview && (
        <div style={{
          padding: '15px',
          background: '#f8f9fa',
          border: '1px solid #e9ecef',
          borderRadius: '4px',
          marginTop: '20px'
        }}>
          <h4>Preview Data:</h4>
          <pre style={{ background: 'white', padding: '10px', borderRadius: '4px', overflow: 'auto', fontSize: '12px' }}>
            {JSON.stringify({
              chapterName,
              locations,
              levelId: `level_${selectedLevel}`,
              chapterId: `chapter_${selectedChapter}`
            }, null, 2)}
          </pre>
        </div>
      )}
    </div>
  );
};

export default UploadChapterLocations;
